/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { handleSettingsChange, otherReportCard } from '@features/downloads/util/custom';
import { useAppSelector } from '../../reducers';
import { isOrgManager } from '../../selectors/user';
import { CardsTotals, HandleDownload, ScopeTotals, VisibilityStatus, DownloadSettingPropType, DownloadSettingsType } from '@g17eco/types/download';
import { DownloadButton } from '@g17eco/molecules/button/DownloadButton';
import { DownloadSettingModal } from './DownloadSettingModal';
import { BuySurveyButton } from './BuySurveyButton';
import {
  DownloadDocx,
  downloadReportHandler,
  DownloadXhtml,
  DownloadXlsxOrCsv
} from './util/downloadReportHandler';
import { getGroup } from '@g17eco/core';
import { DownloadOthersSettingModal } from './DownloadOthersSettingModal';
import { Totals } from './partials/Totals';
import { SURVEY_PACKS, SURVEY_PACKS_SHORT_NAME, getSortedVersions } from '../../constants/standards-frameworks';
import { SurveyScope } from '@g17eco/types/surveyCommon';
import { PACK, SURVEY } from '@constants/terminology';
import { AppConfig } from '@g17eco/types/app';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { getCustomGroupOptions } from '@utils/metricGroup';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { DownloadUtrvStatusCombined } from '@g17eco/types/common';

export interface DownloadCardItem {
  code: string;
  name: string | React.JSX.Element;
  versionGroupCode?: string;
  hideXlsx?: boolean;
}

interface DownloadItem extends DownloadCardItem {
  src?: string;
  icon?: string;
  tooltip?: string;
  disabled?: boolean;
}

interface DownloadCardProps {
  downloadDocx?: DownloadDocx;
  downloadXlsxOrCsv?: DownloadXlsxOrCsv;
  downloadXhtml?: DownloadXhtml;
  item: DownloadItem;
  initiativeId: string;
  isDisabled?: boolean;
  noSurvey?: boolean;
  children?: React.JSX.Element;
  aside?: boolean;
  downloadButton?: React.JSX.Element;
  totals?: ScopeTotals;
  getTotals?: (type: string) => CardsTotals[typeof type];
  surveyScope?: Partial<SurveyScope>;
  defaultDownloadSettings?: Partial<DownloadSettingsType>;
  defaultDownloadOptions?: AppConfig['settings']['defaultDownloadOptions'];
  metricGroups?: MetricGroup[];
}

const getAsideClassName = (aside: boolean) => `download-actions d-flex flex-column ${aside ? 'mt-3 py-0 ml-2 pl-5' : ''}`;

const DownloadCardFooter = (props: DownloadCardProps) => {
  const { aside = false, item, isDisabled } = props;

  const group = getGroup('frameworks', item.code) || getGroup('standards', item.code);
  const isPremium = Boolean(group?.tags?.length);
  const isManager = useAppSelector(isOrgManager);

  let disabledText = '';
  if (isDisabled || item.disabled) {
    if (isPremium) {
      disabledText = isManager
        ? `To access this report, you need to buy the relevant ${SURVEY.ADJECTIVE} ${PACK.SINGULAR} first`
        : `To access this ${SURVEY.ADJECTIVE} ${PACK.SINGULAR}, please contact your organisation's administrator and request they purchase this ${PACK.SINGULAR} for the organisation.`;
    }
  }

  return <aside className={`${getAsideClassName(aside)} text-label mt-1`}>{disabledText}</aside>;
};

export const DownloadCard = (props: DownloadCardProps) => {
  const {
    aside = false,
    item,
    initiativeId,
    downloadButton,
    isDisabled,
    children,
    totals,
    getTotals,
    noSurvey,
    surveyScope,
    defaultDownloadSettings,
    defaultDownloadOptions,
    metricGroups = [],
    ...downloadHandlers
  } = props;

  const code = item.code; // Code means we can download reports
  const isOtherReport = code === otherReportCard.code;
  const renderDownload = downloadHandlers.downloadDocx && (code || isOtherReport);
  const [openSettings, setOpenSettings] = useState(false);

  const handleCloseModal = () => setOpenSettings(false);
  const handleOpenSettings = (e: React.MouseEvent<Element, MouseEvent>) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.blur();
    }
    setOpenSettings(true);
  };

  const [downloadSettings, setDownloadSettings] = useState<DownloadSettingsType>({
    selectedScopes: [],
    visibility: VisibilityStatus.Include,
    status: DownloadUtrvStatusCombined.All,
    ...defaultDownloadSettings,
  });

  const downloadModalMinData: DownloadSettingPropType = {
    title: item.name,
    code: item.code,
    initiativeId,
    isDisabled: isDisabled || noSurvey || item.disabled,
    handleClose: handleCloseModal,
    handleChange: (event) => handleSettingsChange(event, setDownloadSettings, downloadSettings),
    onDownload: ((type, config) =>
      downloadReportHandler({
        type,
        code: config?.packCode || code,
        config,
        downloadSettings,
        ...downloadHandlers,
      })) as HandleDownload,
    downloadSettings,
    isOpen: false,
    defaultDownloadOptions,
  };

  const renderDownloadModal = () => {
    const isInScope = (scopes: string[] | undefined, pack: string) => {
      return (scopes ?? []).includes(pack);
    }

    if (item.versionGroupCode) {
      const versions = getSortedVersions(item.versionGroupCode);
      const [latestVersion, prevVersion] = versions;

      const surveyScopes = surveyScope ? [...(surveyScope.standards ?? []), ...(surveyScope.frameworks ?? [])] : []

      const multiVersionModalProps = {
        ...downloadModalMinData,
        disabledOptions: [],
        getTotals,
        packTitle: 'Version',
        isOpen: Boolean(item.versionGroupCode) && openSettings,
        packOptions: SURVEY_PACKS_SHORT_NAME.filter((pack) => versions.map((i) => i.code).includes(pack.value)),
        defaultPackCode:
          !isInScope(surveyScopes, latestVersion.code) && isInScope(surveyScopes, prevVersion.code)
            ? prevVersion.code
            : latestVersion.code,
      };

      return <DownloadOthersSettingModal {...multiVersionModalProps} />
    }

    if (isOtherReport) {
      const customGroupOptions = getCustomGroupOptions(metricGroups);
      const otherModalProps = {
        ...downloadModalMinData,
        isOpen: isOtherReport && openSettings,
        getTotals,
        packOptions: [...customGroupOptions, ...SURVEY_PACKS],
      };
      return <DownloadOthersSettingModal {...otherModalProps} />
    }

    const downloadModalProps: DownloadSettingPropType = {
      ...downloadModalMinData,
      isOpen: openSettings,
      totals,
    };
    return <DownloadSettingModal {...downloadModalProps} />
  }

  return (
    <div className={`download-card ${aside ? 'd-flex flex-wrap align-self-baseline flex-fill' : ''}`}>
      <section className='download-details d-flex flex-column'>
        <div className='download-details-internal'>
          <div className='mb-auto text-center'>
            {item.src ? <img src={item.src} alt={item.code} width='70px' height='70px' /> : null}
            {item.icon ? <i className={`mt-1 fa ${item.icon} fa-5x`} /> : null}
            <div className='strong mt-4 text-center download-item-name'>{item.name}</div>
          </div>
          <div className='mt-5'>
            {downloadButton ? downloadButton : null}
            {renderDownload ? (
              <SimpleTooltip text={item.tooltip}>
                <DownloadButton
                  testId={`download-report-btn-${code}`}
                  className='doc-btn'
                  color='secondary'
                  outline={true}
                  key={code}
                  onClick={handleOpenSettings}
                  disabled={isDisabled || item.disabled || totals?.total === 0}
                >
                  Download report
                  <i className='fas fa-file-download ml-2' />
                </DownloadButton>
              </SimpleTooltip>
            ) : (
              <BuySurveyButton scopeTag={code} tooltip={item.tooltip} />
            )}
            {children}
          </div>
        </div>
        <Totals totals={totals} />
      </section>
      <DownloadCardFooter {...props} />
      {renderDownloadModal()}
    </div>
  );
};
